import type { FeedArticleProjectionOrdering } from "@/data-access/shared/types/feed-article-projection.types";
import type { SortModelItem } from "ag-grid-community";

/**
 * Maps AG Grid column field names to GraphQL sortable field names
 * Used by both discovery view and news feed for consistent sort ordering
 */
const FIELD_TO_GRAPHQL_MAP: Record<
  string,
  keyof NonNullable<FeedArticleProjectionOrdering[0]>
> = {
  DATE: "datePublished",
  PUBLICATION: "publisher",
  HEADLINE: "headline",
  AUTHOR: "authors",
  IMPACT_SCORE: "impactScore",
  SENTIMENT: "sentimentPolarity",
  PROMINENCE: "prominence",
  TIER: "publisherTier",
  READERSHIP: "publisherReadership",
  DOMAIN_AUTHORITY: "publisherDomainAuthority",
  SOCIAL_ENGAGEMENTS: "socialEngagement",
  CATEGORY: "contentCategory",
  COUNTRY: "publisherCountry",
  REGION: "publisherRegion",
  LANGUAGE: "language",
  NARRATIVE_CLUSTERS: "storyName",
};

/**
 * Converts AG Grid sort model to GraphQL ordering format
 * Only supports single column sorting
 * Uses DESC_NULLS_LAST for descending and ASC_NULLS_LAST for ascending
 *
 * @param sortModel - AG Grid sort model (array of SortModelItem)
 * @returns GraphQL ordering array, or empty array if no valid sort
 *
 * @example
 * ```ts
 * const sortModel = [{ colId: "DATE", sort: "desc" }];
 * const ordering = convertSortModelToOrdering(sortModel);
 * // Returns: [{ datePublished: "DESC_NULLS_LAST" }]
 * ```
 */
export const convertSortModelToOrdering = (
  sortModel: SortModelItem[],
): FeedArticleProjectionOrdering => {
  // Only support single column sorting
  if (sortModel.length === 0) {
    return [];
  }

  const firstSort = sortModel[0];
  if (!firstSort) {
    return [];
  }

  const graphqlField = FIELD_TO_GRAPHQL_MAP[firstSort.colId];

  // If column is not sortable, return empty array
  if (!graphqlField) {
    return [];
  }

  // Convert AG Grid sort direction to GraphQL ordering with null handling
  // Following the pattern from news feed: DESC_NULLS_LAST for desc, ASC_NULLS_LAST for asc
  const orderingValue =
    firstSort.sort === "desc" ? "DESC_NULLS_LAST" : "ASC_NULLS_LAST";

  return [
    {
      [graphqlField]: orderingValue,
    },
  ] as FeedArticleProjectionOrdering;
};

/**
 * Creates an ordering array for a specific GraphQL field and direction
 * Used by news feed to create sort options
 *
 * @param field - GraphQL field name (e.g., "impactScore", "datePublished")
 * @param direction - Sort direction ("asc" | "desc")
 * @returns GraphQL ordering array
 *
 * @example
 * ```ts
 * const ordering = createOrdering("impactScore", "desc");
 * // Returns: [{ impactScore: "DESC_NULLS_LAST" }]
 * ```
 */
export const createOrdering = (
  field: keyof NonNullable<FeedArticleProjectionOrdering[0]>,
  direction: "asc" | "desc",
): FeedArticleProjectionOrdering => {
  const orderingValue =
    direction === "desc" ? "DESC_NULLS_LAST" : "ASC_NULLS_LAST";

  return [
    {
      [field]: orderingValue,
    },
  ] as FeedArticleProjectionOrdering;
};
